/**
 * Whether a recipe payload is safe to cache or treat as fully loaded.
 */
export function isRecipeGenerationReady (recipe) {
  if (!recipe) return false
  const status = recipe.generation_status
  if (status === undefined || status === null) return true
  return status === 'ready'
}

/**
 * 
 * @param {number|string} recipeId - The ID of the recipe to update
 * @param {Object} updates - The fields to update (e.g., { average_rating: 4.5, ratings_count: 10 })
 */
export function updateRecipeInCache(recipeId, updates) {
  const RECIPE_CACHE_KEY = 'recipeCache'
  const RECIPE_DETAIL_CACHE_KEY = 'recipeDetailCache'

  // 1. Update List Cache (recipeCache)
  try {
    const listCacheRaw = sessionStorage.getItem(RECIPE_CACHE_KEY)
    if (listCacheRaw) {
      const listCache = JSON.parse(listCacheRaw)
      let listChanged = false
      
      for (const key in listCache) {
        if (listCache[key] && Array.isArray(listCache[key].recipes)) {
          listCache[key].recipes = listCache[key].recipes.map(recipe => {
            if (String(recipe.id) === String(recipeId)) {
              listChanged = true
              return { ...recipe, ...updates }
            }
            return recipe
          })
        }
      }
      
      if (listChanged) {
        sessionStorage.setItem(RECIPE_CACHE_KEY, JSON.stringify(listCache))
      }
    }
  } catch (err) {
    console.warn('Error updating recipe list cache:', err)
  }

  // 2. Update Detail Cache (recipeDetailCache:slug:locale)
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(RECIPE_DETAIL_CACHE_KEY)) {
        try {
          const cachedRaw = sessionStorage.getItem(key)
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw)
            if (cached && cached.data && String(cached.data.id) === String(recipeId)) {
              cached.data = { ...cached.data, ...updates }
              sessionStorage.setItem(key, JSON.stringify(cached))
            }
          }
        } catch (e) {
          // Skip invalid JSON or other issues
        }
      }
    }
  } catch (err) {
    console.warn('Error updating recipe detail cache:', err)
  }
}

/**
 * Saves a recipe to the detail cache
 * 
 * @param {Object} recipe - The recipe object
 * @param {string} locale - The locale of the recipe
 */
export function saveRecipeToDetailCache(recipe, locale) {
  if (!recipe || !recipe.slug || !locale) return
  if (!isRecipeGenerationReady(recipe)) return
  
  const RECIPE_DETAIL_CACHE_KEY = 'recipeDetailCache'
  const cacheKey = `${RECIPE_DETAIL_CACHE_KEY}:${recipe.slug}:${locale}`
  
  try {
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data: recipe,
        ts: Date.now(),
      })
    )
  } catch (e) {
    console.warn('Error saving to recipe detail cache:', e)
  }
}

/**
 * Saves multiple recipes to the detail cache
 * 
 * @param {Array} recipes - Array of recipe objects
 * @param {string} locale - The locale of the recipes
 */
export function saveRecipesToDetailCache(recipes, locale) {
  if (!Array.isArray(recipes) || !locale) return
  
  recipes.forEach(recipe => {
    saveRecipeToDetailCache(recipe, locale)
  })
}

/**
 * Finds a recipe by slug in the list cache
 * 
 * @param {string} slug - The slug of the recipe to find
 * @returns {Object|null} - The recipe object or null if not found
 */
export function findRecipeInListCache(slug) {
  const RECIPE_CACHE_KEY = 'recipeCache'
  try {
    const listCacheRaw = sessionStorage.getItem(RECIPE_CACHE_KEY)
    if (listCacheRaw) {
      const listCache = JSON.parse(listCacheRaw)
      for (const key in listCache) {
        if (listCache[key] && Array.isArray(listCache[key].recipes)) {
          const found = listCache[key].recipes.find(recipe => String(recipe.slug) === String(slug))
          if (found && isRecipeGenerationReady(found)) {
            return found
          }
        }
      }
    }
  } catch (err) {
    console.warn('Error reading recipe list cache:', err)
  }
  return null
}

